import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, AlertTriangle, Scale, UserCheck, Ban, Copyright, RefreshCw, ChevronRight } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-8"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-peace-blue/20 rounded-lg flex items-center justify-center">
        <Icon className="text-peace-blue" size={20} />
      </div>
      <h2 className="font-bebas text-2xl text-white tracking-wider">{title}</h2>
    </div>
    <div className="text-gray-300 font-inter text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </motion.section>
);

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-night-count">
      {/* Header */}
      <div className="bg-gradient-to-r from-peace-blue/20 via-gorkhali-red/10 to-peace-blue/20 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FileText className="mx-auto mb-4 text-peace-blue" size={48} />
            <h1 className="font-bebas text-5xl text-white tracking-wider mb-4">
              TERMS OF SERVICE
            </h1>
            <p className="text-gray-400 font-inter">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8"
        >
          <p className="text-gray-300 font-inter leading-relaxed">
            Welcome to Nepali Ballot. These Terms of Service ("Terms") govern your access to and use of nepaliballot.com (the "Website") and all related services. By accessing or using our Website, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Website.
          </p>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-marigold/10 border border-marigold/30 rounded-lg p-6 mb-8"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-marigold flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-bebas text-xl text-marigold tracking-wider mb-2">IMPORTANT NOTICE</h3>
              <p className="text-gray-300 font-inter text-sm">
                Nepali Ballot is an <strong>ELECTION SIMULATION PLATFORM</strong> designed for educational and entertainment purposes only. This is NOT an official government election platform. Votes cast on this Website have NO legal effect and are NOT part of any official election process in Nepal.
              </p>
            </div>
          </div>
        </motion.div>

        <Section icon={UserCheck} title="ACCEPTANCE OF TERMS">
          <p>By using Nepali Ballot, you acknowledge that you have read, understood, and agree to be bound by these Terms. You also agree to comply with all applicable laws and regulations.</p>
          <p>If you are using our Website on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
        </Section>

        <Section icon={FileText} title="DESCRIPTION OF SERVICE">
          <p>Nepali Ballot provides:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>An election simulation platform for Nepal's electoral process</li>
            <li>Information about candidates, constituencies, and political parties</li>
            <li>Historical election data and educational content</li>
            <li>Interactive games and quizzes about Nepal's democracy</li>
            <li>News aggregation from various sources</li>
          </ul>
          <p className="mt-3">All services are provided "as is" without warranty of any kind.</p>
        </Section>

        <Section icon={Scale} title="USER CONDUCT">
          <p>When using our Website, you agree NOT to:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Use the Website for any illegal or unauthorized purpose</li>
            <li>Attempt to manipulate voting results through automated means</li>
            <li>Impersonate any person or entity</li>
            <li>Interfere with or disrupt the Website's operation</li>
            <li>Upload malicious code or attempt to gain unauthorized access</li>
            <li>Use the Website to spread misinformation about elections</li>
            <li>Collect or harvest user data without permission</li>
            <li>Violate any applicable local, national, or international law</li>
          </ul>
        </Section>

        <Section icon={Copyright} title="INTELLECTUAL PROPERTY">
          <p><strong className="text-white">Our Content:</strong> All content on Nepali Ballot, including text, graphics, logos, designs, and software, is owned by or licensed to us and is protected by copyright, trademark, and other intellectual property laws.</p>
          <p><strong className="text-white">Third-Party Content:</strong> Candidate information, news articles, and images may be sourced from third parties and remain the property of their respective owners. We use such content under fair use principles for educational purposes.</p>
          <p><strong className="text-white">Your Use:</strong> You may view, download, and print content from our Website for personal, non-commercial use only. Any other use requires our prior written consent.</p>
        </Section>

        <Section icon={Ban} title="DISCLAIMERS">
          <p><strong className="text-white">Simulation Only:</strong> This Website is a simulation for educational and entertainment purposes. It has no connection to the Election Commission of Nepal or any official electoral body.</p>
          <p><strong className="text-white">No Warranty:</strong> We provide our services "as is" and "as available" without any warranties, express or implied. We do not guarantee the accuracy, completeness, or reliability of any content.</p>
          <p><strong className="text-white">Third-Party Links:</strong> Our Website may contain links to third-party websites. We are not responsible for the content or practices of these external sites.</p>
        </Section>

        <Section icon={Scale} title="LIMITATION OF LIABILITY">
          <p>To the maximum extent permitted by law, Nepali Ballot and its operators shall not be liable for:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Any indirect, incidental, special, or consequential damages</li>
            <li>Loss of profits, data, or goodwill</li>
            <li>Any damages arising from your use of the Website</li>
            <li>Any errors or omissions in content</li>
            <li>Any unauthorized access to or alteration of your data</li>
          </ul>
        </Section>

        <Section icon={RefreshCw} title="CHANGES TO TERMS">
          <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page. Your continued use of the Website after any changes constitutes your acceptance of the new Terms.</p>
          <p>We encourage you to review these Terms periodically for any updates.</p>
        </Section>

        {/* Governing Law */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8"
        >
          <h3 className="font-bebas text-xl text-white tracking-wider mb-3">GOVERNING LAW</h3>
          <p className="text-gray-300 font-inter text-sm">
            These Terms shall be governed by and construed in accordance with the laws of Nepal, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved through appropriate legal channels in Nepal.
          </p>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-peace-blue/10 border border-peace-blue/30 rounded-lg p-6"
        >
          <h3 className="font-bebas text-xl text-white tracking-wider mb-3">CONTACT INFORMATION</h3>
          <p className="text-gray-300 font-inter text-sm mb-3">
            If you have any questions about these Terms, please contact us:
          </p>
          <p className="text-gray-300 font-inter text-sm">
            <strong className="text-white">Email:</strong> buildersadventure00@gmail.com
          </p>
          <p className="text-gray-300 font-inter text-sm">
            <strong className="text-white">Website:</strong> <Link to="/contact" className="text-marigold hover:underline">nepaliballot.com/contact</Link>
          </p>
        </motion.div>

        {/* Navigation */}
        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/privacy" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            Privacy Policy <ChevronRight size={16} />
          </Link>
          <Link 
            to="/disclaimer" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            Disclaimer <ChevronRight size={16} />
          </Link>
          <Link 
            to="/about" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            About Us <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">© 2026 Nepali Ballot • All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
